import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Check, UserPlus } from 'lucide-react';

const CustomerSelect = ({ customers, selectedCustomer, onChange, onAddNewClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Convert selected customer ID to string if it's an object
  const selectedCustomerId = typeof selectedCustomer === 'object' 
    ? selectedCustomer._id 
    : selectedCustomer;

  // Find selected customer info
  const selectedCustomerInfo = customers.find(c => c._id === selectedCustomerId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-gray-700">Customer *</label>
        <button
          type="button"
          onClick={onAddNewClick}
          className="inline-flex items-center px-2 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          <UserPlus className="h-4 w-4 mr-1" />
          Add New Customer
        </button>
      </div>

      <div
        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        {/* Selected Value Display */}
        <div className="min-h-[2.5rem] px-3 py-2 flex items-center justify-between">
          {selectedCustomerInfo ? (
            <div>
              <div className="text-sm font-medium text-gray-900">
                {selectedCustomerInfo.name}
              </div>
              {selectedCustomerInfo.isCompany && selectedCustomerInfo.taxId && (
                <div className="text-xs text-gray-500">
                  Tax ID: {selectedCustomerInfo.taxId}
                </div>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-500">Select customer...</span>
          )}
          {selectedCustomerId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="ml-2 text-gray-400 hover:text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg">
          {/* Search Box */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="pl-8 pr-4 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Customer List */}
          <div className="max-h-60 overflow-y-auto">
            {customers
              .filter(customer => 
                customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.taxId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((customer) => (
                <div
                  key={customer._id}
                  className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer ${
                    selectedCustomerId === customer._id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => {
                    onChange(customer._id);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center flex-1">
                    <div className="w-4 h-4 border rounded mr-3 flex items-center justify-center">
                      {selectedCustomerId === customer._id && (
                        <Check className="h-3 w-3 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{customer.name}</span>
                      <div className="flex flex-col">
                        {customer.isCompany && customer.taxId && (
                          <span className="text-xs text-gray-500">Tax ID: {customer.taxId}</span>
                        )}
                        {customer.email && (
                          <span className="text-xs text-gray-500">{customer.email}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {customers.filter(customer => 
              customer.name.toLowerCase().includes(searchTerm.toLowerCase())
            ).length === 0 && (
              <div className="px-4 py-4 text-center">
                <p className="text-sm text-gray-500">No customers found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelect;